import type {
  AccessGroupItem,
  AccessTemplateDto,
  CatalogTemplateRef,
  TemplateScopeMode,
  UserAccessEntry,
} from "@/features/identity-hub/api/identity-hub.api";
import { filterGrantableByPermissions, type PermissionTuple } from "./grantable-role";

export type AccessScopeFields = {
  templateId?: string | null;
  template?: CatalogTemplateRef | null;
};

export function catalogTemplateId(access: AccessScopeFields): string | null {
  return access.template?.id ?? access.templateId ?? null;
}

export function catalogScopeMode(access: AccessScopeFields): TemplateScopeMode | null {
  return access.template?.scopeMode ?? null;
}

/** Live group projected from a FIXED catalog role — grant/revoke is bulk. */
export function isFixedCatalogAccess(access: AccessScopeFields): boolean {
  return catalogScopeMode(access) === "FIXED";
}

/** WILDCARD catalog projections and unlinked/custom groups — per-tenant assignable. */
export function isPerTenantAssignable(access: AccessScopeFields): boolean {
  return catalogScopeMode(access) !== "FIXED";
}

export function perTenantAssignableGroups<T extends AccessScopeFields>(groups: T[]): T[] {
  return groups.filter((group) => isPerTenantAssignable(group));
}

export function uniqueFixedCatalogRoles(entries: UserAccessEntry[]): Array<{
  templateId: string;
  name: string;
  orgIds: string[];
}> {
  const map = new Map<string, { templateId: string; name: string; orgIds: string[] }>();
  for (const entry of entries) {
    if (!isFixedCatalogAccess(entry.access)) continue;
    const templateId = catalogTemplateId(entry.access);
    if (!templateId) continue;
    const prev = map.get(templateId);
    if (prev) {
      if (!prev.orgIds.includes(entry.access.tenantId)) prev.orgIds.push(entry.access.tenantId);
    } else {
      map.set(templateId, {
        templateId,
        name: entry.access.name,
        orgIds: [entry.access.tenantId],
      });
    }
  }
  return [...map.values()];
}

/** True when this user already holds `templateId` on every listed org. */
export function userHoldsRoleOnTenants(
  entries: UserAccessEntry[] | undefined,
  templateId: string,
  tenantIds: string[],
): boolean {
  if (tenantIds.length === 0) return false;
  const held = new Set(
    (entries ?? [])
      .filter((entry) => catalogTemplateId(entry.access) === templateId)
      .map((entry) => entry.access.tenantId),
  );
  return tenantIds.every((id) => held.has(id));
}

/** True when every org on the FIXED catalog role already has this user. */
export function userHoldsFullFixedRole(
  entries: UserAccessEntry[] | undefined,
  template: Pick<AccessTemplateDto, "id" | "tenantIds">,
): boolean {
  return userHoldsRoleOnTenants(entries, template.id, template.tenantIds);
}

/**
 * WILDCARD catalog projections an org admin can assign even when Adge owns
 * the template. Unlinked custom groups and FIXED projections are skipped —
 * those stay on person/org drawers and owned GET /templates respectively.
 */
export function wildcardTemplatesFromLiveGroups(
  groups: Array<
    Pick<AccessGroupItem, "name" | "permissions" | "createdAt" | "updatedAt">
    & AccessScopeFields
    & { description?: string | null; deletedAt?: string | null }
  >,
): AccessTemplateDto[] {
  const map = new Map<string, AccessTemplateDto>();
  for (const group of groups) {
    if (group.deletedAt) continue;
    if (catalogScopeMode(group) !== "WILDCARD") continue;
    const templateId = catalogTemplateId(group);
    if (!templateId || map.has(templateId)) continue;
    map.set(templateId, {
      id: templateId,
      name: group.name,
      description: group.description ?? null,
      scopeMode: "WILDCARD",
      status: "ACTIVE",
      createdByUserId: null,
      canBeEditedByTenantId: null,
      createdAt: group.createdAt,
      updatedAt: group.updatedAt,
      tenantIds: [],
      tenants: [],
      ownerTenant: null,
      permissions: group.permissions,
    });
  }
  return [...map.values()];
}

/** Owned catalog rows plus live WILDCARD projections the list endpoint hides. */
export function mergeAssignableCatalogRoles(
  catalog: AccessTemplateDto[],
  liveGroups: Parameters<typeof wildcardTemplatesFromLiveGroups>[0],
): AccessTemplateDto[] {
  const fromCatalog = catalog.filter((row) => row.status === "ACTIVE");
  const seen = new Set(fromCatalog.map((row) => row.id));
  const extras = wildcardTemplatesFromLiveGroups(liveGroups).filter((row) => !seen.has(row.id));
  return [...fromCatalog, ...extras];
}

/**
 * Amber `tenantId` for catalog grant: wildcards act in a caller-scoped org,
 * FIXED still acts as the owning organization.
 */
export function actingTenantIdForCatalogGrant(opts: {
  scopeMode: TemplateScopeMode;
  ownerTenantId: string | null | undefined;
  selectedTenantIds?: string[];
  activeTenantId: string | null;
}): string | null {
  if (opts.scopeMode === "WILDCARD") {
    return opts.selectedTenantIds?.[0] ?? opts.activeTenantId;
  }
  return opts.ownerTenantId ?? opts.activeTenantId;
}

/** Per-tenant assignable groups whose permissions the caller may grant. */
export function grantablePerTenantGroups<
  T extends AccessScopeFields & { permissions?: PermissionTuple[]; deletedAt?: string | null },
>(groups: T[], grantableKeys: ReadonlySet<string> | null): T[] {
  return filterGrantableByPermissions(
    perTenantAssignableGroups(groups).filter((group) => !group.deletedAt),
    grantableKeys,
  );
}

/**
 * Roles a caller may assign on one organization.
 * Fixed catalog roles stay off the per-org picker unless the caller is a
 * platform admin, who grants them through the catalog (template) path.
 */
export function grantableRolesOnOrg<
  T extends AccessScopeFields & { permissions?: PermissionTuple[]; deletedAt?: string | null },
>(
  groups: T[],
  grantableKeys: ReadonlySet<string> | null,
  options?: { includeFixedCatalog?: boolean },
): T[] {
  const active = groups.filter((group) => !group.deletedAt);
  const perTenant = grantablePerTenantGroups(active, grantableKeys);
  if (!options?.includeFixedCatalog) return perTenant;
  const fixed = filterGrantableByPermissions(
    active.filter((group) => isFixedCatalogAccess(group)),
    grantableKeys,
  );
  return [...perTenant, ...fixed];
}

/**
 * Unique grantable org-role names for the People toolbar, optionally scoped to one org.
 * FIXED catalog roles stay out unless the caller can grant templates.
 */
export function peopleRoleFilterOptions(
  groupsByTenant: Map<string, AccessGroupItem[]> | Record<string, AccessGroupItem[]>,
  grantableKeys: ReadonlySet<string> | null,
  tenantId?: string | null,
  options?: { includeFixedCatalog?: boolean },
): string[] {
  const groupsFor = (id: string): AccessGroupItem[] =>
    groupsByTenant instanceof Map ? (groupsByTenant.get(id) ?? []) : (groupsByTenant[id] ?? []);
  const tenantIds = tenantId
    ? [tenantId]
    : groupsByTenant instanceof Map
      ? [...groupsByTenant.keys()]
      : Object.keys(groupsByTenant);
  const names = new Set<string>();
  for (const id of tenantIds) {
    for (const group of grantableRolesOnOrg(groupsFor(id), grantableKeys, options)) {
      names.add(group.name);
    }
  }
  return [...names].sort((a, b) => a.localeCompare(b));
}

export type PeopleRoleFilterSnapshot = {
  scopeKey: string;
  roles: string[];
};

/**
 * Holds the People role filter on the last complete grantable list.
 * Access groups arrive in chunks, so a partial map can contain a single role
 * and then a different set on the next render. Incomplete results must not
 * replace a settled list for the same org scope.
 */
export function settlePeopleRoleFilter(
  previous: PeopleRoleFilterSnapshot,
  next: PeopleRoleFilterSnapshot & { isComplete: boolean },
): PeopleRoleFilterSnapshot {
  if (!next.isComplete) {
    return previous.scopeKey === next.scopeKey ? previous : { scopeKey: next.scopeKey, roles: [] };
  }
  if (previous.scopeKey === next.scopeKey && sameStringList(previous.roles, next.roles)) {
    return previous;
  }
  return { scopeKey: next.scopeKey, roles: next.roles };
}

/**
 * Selected People role once the grantable list has settled.
 * An incomplete list keeps the selection — the new org may still offer that name.
 * A complete list returns null when the name is no longer offered (Any role).
 */
export function settleSelectedPeopleRole(
  selectedRole: string | null,
  settled: { roles: readonly string[]; isComplete: boolean },
): string | null {
  if (selectedRole == null || !settled.isComplete) return selectedRole;
  return settled.roles.includes(selectedRole) ? selectedRole : null;
}

function sameStringList(left: string[], right: string[]): boolean {
  if (left.length !== right.length) return false;
  for (let index = 0; index < left.length; index += 1) {
    if (left[index] !== right[index]) return false;
  }
  return true;
}
